import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_BUSINESS } from '~/constants/defaultStates';
import { STATUS } from '~/literals';
import { analytics } from '~/services/analyticsService';
import { IUserDetails, UserState } from '~/types';
import { IBusiness } from '~/types/business';
import { PlanType } from '~/utils/payment';

export const userState: UserState = {
  isAuthenticated: false,
  status: STATUS.IDLE,
  token: '',
  businessDetails: {
    ...DEFAULT_BUSINESS,
  },
  first_name: '',
  last_name: '',
  email: '',
  user_id: null,
  isSidebarOpen: false,
  pendingPlan: null,
  paymentInitiated: false,
  paymentCompleted: false,
  currentPlan: null,
  companies: [],
};

interface ILoginResult {
  token: string;
  userBusiness: UserState['businessDetails'] | null;
  first_name: string;
  last_name: string;
  email: string;
  user_id: number;
  current_plan: PlanType | null;
  companies?: IBusiness[];
}

export const userSlice = createSlice({
  name: 'user',
  initialState: userState,
  reducers: {
    login: (draft, action: PayloadAction<{ email: string; password: string }>) => {
      console.log('Logging in ', action.payload.email);
      draft.status = STATUS.RUNNING;
    },
    loginSuccess: (draft, action: PayloadAction<ILoginResult>) => {
      draft.isAuthenticated = true;
      draft.status = STATUS.READY;
      draft.token = action.payload.token;
      draft.first_name = action.payload.first_name;
      draft.last_name = action.payload.last_name;
      draft.email = action.payload.email;
      draft.user_id = action.payload.user_id;
      draft.currentPlan = action.payload.current_plan;
      if (action.payload.userBusiness) {
        draft.businessDetails = action.payload.userBusiness;
      }
      if (action.payload.companies) {
        draft.companies = action.payload.companies;
      }

      analytics.identify(action.payload.user_id.toString(), {
        email: action.payload.email,
        first_name: action.payload.first_name,
        last_name: action.payload.last_name,
        plan_type: action.payload.current_plan || 'free',
      });
    },
    loginFailure: draft => {
      draft.isAuthenticated = false;
      draft.status = STATUS.IDLE;
    },
    googleLogin: (draft, action: PayloadAction<{ access_token: string }>) => {
      console.log('Logging in with google', action.type);
      draft.status = STATUS.RUNNING;
    },
    googleLoginSuccess: (draft, action: PayloadAction<ILoginResult>) => {
      draft.isAuthenticated = true;
      draft.status = STATUS.READY;
      draft.token = action.payload.token;
      draft.first_name = action.payload.first_name;
      draft.last_name = action.payload.last_name;
      draft.email = action.payload.email;
      draft.user_id = action.payload.user_id;
      draft.currentPlan = action.payload.current_plan;
      if (action.payload.userBusiness) {
        draft.businessDetails = action.payload.userBusiness;
      }
      if (action.payload.companies) {
        draft.companies = action.payload.companies;
      }

      analytics.identify(action.payload.user_id.toString(), {
        email: action.payload.email,
        first_name: action.payload.first_name,
        last_name: action.payload.last_name,
        plan_type: action.payload.current_plan || 'free',
      });
    },
    googleLoginFailure: draft => {
      draft.isAuthenticated = false;
      draft.status = STATUS.IDLE;
    },
    logOut: draft => {
      draft.status = STATUS.RUNNING;
    },
    logOutSuccess: draft => {
      draft.isAuthenticated = false;
      draft.status = STATUS.IDLE;
      draft.token = '';
      draft.user_id = null;
      draft.currentPlan = null;
      draft.businessDetails = { ...DEFAULT_BUSINESS };
      draft.companies = [];

      analytics.reset();
    },
    changeBusiness: (draft, action: PayloadAction<Partial<IBusiness>>) => {
      draft.businessDetails = { ...draft.businessDetails, ...action.payload };
    },
    changeSidebar: (draft, action: PayloadAction<boolean>) => {
      draft.isSidebarOpen = action.payload;
    },
    setPendingPlan: (draft, action: PayloadAction<PlanType | null>) => {
      draft.pendingPlan = action.payload;
    },
    setPaymentInitiated: (draft, action: PayloadAction<boolean>) => {
      draft.paymentInitiated = action.payload;
    },
    setPaymentCompleted: (draft, action: PayloadAction<boolean>) => {
      draft.paymentCompleted = action.payload;
      if (action.payload) {
        draft.pendingPlan = null;
        draft.paymentInitiated = false;
      }
    },
    clearPaymentState: draft => {
      draft.pendingPlan = null;
      draft.paymentInitiated = false;
      draft.paymentCompleted = false;
    },
    setCurrentPlan: (draft, action: PayloadAction<PlanType | null>) => {
      draft.currentPlan = action.payload;
    },
    setCompanies: (draft, action: PayloadAction<IBusiness[]>) => {
      draft.companies = action.payload;
    },
    addCompany: (draft, action: PayloadAction<IBusiness>) => {
      draft.companies.push(action.payload);
    },
    selectCompany: (draft, action: PayloadAction<string>) => {
      // Set the selected company as the current business details
      const selectedCompany = draft.companies.find(
        company => company.business_uuid === action.payload,
      );
      if (selectedCompany) {
        draft.businessDetails = selectedCompany;
      }
    },
    fetchCurrentUser: draft => {
      draft.status = STATUS.RUNNING;
    },
    fetchCurrentUserSuccess: (draft, action: PayloadAction<IUserDetails>) => {
      draft.status = STATUS.READY;
      draft.currentPlan = action.payload.current_plan;
      draft.user_id = action.payload.user_id;
      if (action.payload.user_business) {
        draft.businessDetails = action.payload.user_business;
      }
      draft.companies = [...action.payload.companies];
    },
    fetchCurrentUserFailure: draft => {
      draft.status = STATUS.IDLE;
    },
    tokenLogin: (draft, action: PayloadAction<{ token: string }>) => {
      console.log('Logging in with token: ', action.payload.token);
      draft.status = STATUS.RUNNING;
    },
    tokenLoginSuccess: (draft, action: PayloadAction<ILoginResult>) => {
      draft.isAuthenticated = true;
      draft.status = STATUS.READY;
      draft.token = action.payload.token;
      draft.first_name = action.payload.first_name;
      draft.last_name = action.payload.last_name;
      draft.email = action.payload.email;
      draft.user_id = action.payload.user_id;
      draft.currentPlan = action.payload.current_plan;
      if (action.payload.userBusiness) {
        draft.businessDetails = action.payload.userBusiness;
      }
      if (action.payload.companies) {
        draft.companies = action.payload.companies;
      }

      analytics.identify(action.payload.user_id.toString(), {
        email: action.payload.email,
        first_name: action.payload.first_name,
        last_name: action.payload.last_name,
        plan_type: action.payload.current_plan || 'free',
      });
    },
    tokenLoginFailure: draft => {
      draft.isAuthenticated = false;
      draft.status = STATUS.IDLE;
    },
  },
});

export const {
  login,
  loginSuccess,
  loginFailure,
  googleLogin,
  googleLoginSuccess,
  googleLoginFailure,
  logOut,
  logOutSuccess,
  changeBusiness,
  changeSidebar,
  setPendingPlan,
  setPaymentInitiated,
  setPaymentCompleted,
  clearPaymentState,
  setCurrentPlan,
  setCompanies,
  addCompany,
  selectCompany,
  fetchCurrentUser,
  fetchCurrentUserSuccess,
  fetchCurrentUserFailure,
  tokenLogin,
  tokenLoginSuccess,
  tokenLoginFailure,
} = userSlice.actions;

export default userSlice.reducer;
